<script lang="ts">
	import type { InputCheckboxProps, InputProps } from './types';
	import { requiredRule } from '$lib/utils/forms';
	import { attrValue, shortId } from '$lib/utils/html';
	import { undefOrNull } from '$lib/utils/objects';
	import InputLabel from './InputLabel.svelte';
	import { onDestroy } from 'svelte';

	export let props: InputProps | null = null;
	export let isLabelHidden: boolean | null = null;

	const isInputCheckboxProps = (arg: any): arg is InputCheckboxProps => {
		return !!arg && arg.key && arg.key === 'checkbox';
	};
	$: if (props) {
		props.validation = props.validation ?? { rules: [] };
		if (props.validation.rules) {
			let hasRequiredRules = props.validation.rules.some((r) => r.key === 'required');
			if (props.required && !hasRequiredRules) {
				props.validation = {
					...props.validation,
					rules: [...props.validation.rules, requiredRule()]
				};
			} else if (!props.required) {
				props.validation = {
					...props.validation,
					rules: []
				};
			}
		}
	}

	$: if (props && props.validation) {
		let isValid = true;
		if (props.validation.rules?.length) {
			props.validation.rules.forEach((rule, index) => {
				let isRuleValid =
					!!rule.check &&
					((!undefOrNull(props.value) && rule.check(props.value)) ||
						(isInputCheckboxProps(props) && rule.check(props.checked)));
				if (props.validation?.rules && props.validation.rules[index]) {
					props.validation.rules[index] = { ...rule, valid: isRuleValid };
					isValid = isValid && isRuleValid;
				}
			});
		}
		props.validation.valid = isValid;
	}

	$: if (props) {
		props.id = props && props.id ? props.id : `input_${shortId()}`;
	}

	onDestroy(() => {
		if (props) {
			props.touched = false;
			props.dirty = false;
			props.focus = false;
			props.touchedTwice = false;
			delete props.validation;
		}
	});
</script>

{#if props}
	<div class={attrValue([props.classNames, 'w-full'])}>
		{#if !isLabelHidden && (props.icon || props.label || props.hint)}
			<InputLabel {props} />
		{/if}

		<slot />

		{#if props.error}
			<p class="text-sm text-danger mt-1 ml-1">{props.error}</p>
		{/if}
		{#if props.warning}
			<p class="text-sm text-warning mt-1 ml-1">{props.warning}</p>
		{/if}

		{#if props.description}
			<p class="mt-1 italic text-xs">{@html props.description}</p>
		{/if}
	</div>
{/if}

<style lang="postcss">
	div {
		& :global(.input),
		& :global(.textarea),
		& :global(.select) {
			filter: none;
			@apply transition block w-full border rounded-lg pr-6 placeholder-grey;
			@apply outline-none ring-0 focus:outline-none focus:ring-0;
		}

		& :global(.input),
		& :global(.select) {
			@apply h-[50px];
		}

		& :global(.input::-webkit-input-placeholder) {
			font-weight: 300;
		}

		& :global(.input::-moz-placeholder) {
			font-weight: 300;
		}

		& :global(.input::-ms-input-placeholder) {
			font-weight: 300;
		}

		& :global(.textarea::-webkit-input-placeholder) {
			font-weight: 300;
		}

		& :global(.textarea::-moz-placeholder) {
			font-weight: 300;
		}

		& :global(.textarea::-ms-input-placeholder) {
			font-weight: 300;
		}
	}
</style>
